import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import type { InputChangeEvent } from "@tql/ui-components-react/kendo/inputs";
import { Input } from "@tql/ui-components-react/kendo/inputs";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { Skeleton } from "@tql/ui-components-react/tql/indicators";
import {
	edit_05,
	searchSmLine,
	trash_01,
} from "@tql/ui-components-react/untitled/icons";
import type { UIEvent } from "react";
import { useEffect, useMemo, useState } from "react";

import type { UnifiedNotificationRecipient } from "~/shared/schemas/shipment-notification-schema.ts";

import styles from "./mobile-notifications.module.css";

type MobileNotificationsProps = {
	readonly currentUserEmail?: string;
	readonly data: UnifiedNotificationRecipient[];
	readonly isAdmin?: boolean;
	readonly loading?: boolean;
	readonly onDelete: (item: UnifiedNotificationRecipient) => void;
	readonly onEdit: (item: UnifiedNotificationRecipient) => void;
};

const LOADING_CARD_COUNT = 6;
const PAGE_SIZE = 25;
const SCROLL_THRESHOLD = 2;

const LoadingNotificationCard = () => (
	<div
		className={styles.notificationCard}
		data-testid={"notification-card-skeleton"}
	>
		<div className={styles.cardRow}>
			<div className={styles.cardColumn}>
				<div className={styles.fieldLabel}>User</div>
				<span className={styles.skeletonText}>
					<Skeleton animation={{ type: "wave" }} shape={"text"} />
				</span>
			</div>
			<div className={styles.cardColumn}>
				<div className={styles.fieldLabel}>Email</div>
				<span className={styles.skeletonText}>
					<Skeleton animation={{ type: "wave" }} shape={"text"} />
				</span>
			</div>
			<div className={styles.cardColumn}>
				<div className={styles.fieldLabel}>Subscribed</div>
				<span className={styles.skeletonText}>
					<Skeleton animation={{ type: "wave" }} shape={"text"} />
				</span>
			</div>
		</div>
		<div className={styles.actionsRow}>
			<span className={styles.skeletonAction}>
				<Skeleton animation={{ type: "wave" }} shape={"rectangle"} />
			</span>
			<span className={styles.skeletonAction}>
				<Skeleton animation={{ type: "wave" }} shape={"rectangle"} />
			</span>
		</div>
	</div>
);

export const MobileNotifications = ({
	currentUserEmail,
	data,
	isAdmin,
	loading,
	onDelete,
	onEdit,
}: MobileNotificationsProps) => {
	const [searchTerm, setSearchTerm] = useState("");
	const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

	const filteredRecipients = useMemo(() => {
		const term = searchTerm.trim().toLowerCase();
		if (!term) return data;
		return data.filter((recipient) => {
			const fullName =
				`${recipient.firstName ?? ""} ${recipient.lastName ?? ""}`
					.trim()
					.toLowerCase();
			return [
				recipient.firstName,
				recipient.lastName,
				recipient.email,
				fullName,
			]
				.filter(Boolean)
				.some((field) => field?.toLowerCase().includes(term));
		});
	}, [searchTerm, data]);

	useEffect(() => {
		setVisibleCount(PAGE_SIZE);
	}, [searchTerm, data]);

	const visibleRecipients = useMemo(
		() =>
			filteredRecipients.slice(
				0,
				Math.min(visibleCount, filteredRecipients.length),
			),
		[filteredRecipients, visibleCount],
	);

	const handleSearchChange = (event: InputChangeEvent) => {
		setSearchTerm(String(event.value ?? ""));
	};

	const handleListScroll = (event: UIEvent<HTMLDivElement>) => {
		if (visibleCount >= filteredRecipients.length) return;
		const target = event.currentTarget;
		const distanceFromBottom =
			target.scrollHeight - (target.scrollTop + target.clientHeight);
		if (distanceFromBottom <= SCROLL_THRESHOLD) {
			setVisibleCount((count) =>
				Math.min(count + PAGE_SIZE, filteredRecipients.length),
			);
		}
	};

	return (
		<div className={styles.mobileNotifications}>
			<div className={styles.searchWrapper}>
				<Input
					aria-label={"Search notifications"}
					className={styles.searchInput}
					data-testid={"notification-card-search-input"}
					disabled={loading}
					onChange={handleSearchChange}
					placeholder={"Search"}
					value={searchTerm}
				/>
				<SvgIcon className={styles.searchIcon} icon={searchSmLine} />
			</div>
			<div
				aria-busy={loading}
				className={styles.listScrollRegion}
				data-testid={"notification-card-scroll-region"}
				onScroll={handleListScroll}
			>
				{loading ? (
					Array.from({ length: LOADING_CARD_COUNT }, (_, index) => (
						<LoadingNotificationCard key={`loading-${index}`} />
					))
				) : (
					<>
						{filteredRecipients.length === 0 ? (
							<div className={styles.emptyMessage}>
								No matching notifications
							</div>
						) : null}
						{visibleRecipients.map((recipient) => {
							const canModify =
								isAdmin === true ||
								recipient.email.toLowerCase() ===
									currentUserEmail?.toLowerCase();

							return (
								<div
									key={recipient.id}
									className={styles.notificationCard}
									data-testid={"notification-card"}
								>
									<div className={styles.cardRow}>
										<div className={styles.cardColumn}>
											<div className={styles.fieldLabel}>User</div>
											<div className={styles.fieldValue}>
												{`${recipient.firstName ?? ""} ${recipient.lastName ?? ""}`.trim()}
											</div>
										</div>
										<div className={styles.cardColumn}>
											<div className={styles.fieldLabel}>Email</div>
											<div className={styles.fieldValue}>{recipient.email}</div>
										</div>
										<div className={styles.cardColumn}>
											<div className={styles.fieldLabel}>Subscribed</div>
											<div className={styles.fieldValue}>
												{recipient.subscribedToAll ? "Yes" : "No"}
											</div>
										</div>
									</div>
									<div className={styles.actionsRow}>
										<Button
											aria-label={`Edit ${recipient.email}`}
											data-testid={"notification-card-edit-button"}
											disabled={!canModify}
											onClick={() => onEdit(recipient)}
											svgIcon={edit_05}
											variant={"tertiary"}
										/>
										<Button
											aria-label={`Delete ${recipient.email}`}
											data-testid={"notification-card-delete-button"}
											disabled={!canModify}
											onClick={() => onDelete(recipient)}
											svgIcon={trash_01}
											variant={"dangerTertiary"}
										/>
									</div>
								</div>
							);
						})}
					</>
				)}
			</div>
		</div>
	);
};
